// Framework
import { Injectable } from '@angular/core';
import { Http, Headers, RequestOptions } from '@angular/http';
import { map, timeout } from 'rxjs/operators';

// Providers
import { AngularFirestore} from '@angular/fire/firestore';
import * as firebase from 'firebase/app';
import { AppService } from './app.service';

// Model
import { SignUpInfo } from '../model/SignUpInfo';
import * as moment from 'moment';

@Injectable()
export class DataService {
    loggedInUserUid: string;
    signUpInfo: SignUpInfo;
    daycare: any;
    cpDiscounts: any[] = [];

    IS_BETA: boolean;

    constructor(
        private afs: AngularFirestore,
        private appSvc: AppService,
        private http: Http,
    ) {
        this.signUpInfo = Object.assign({}, new SignUpInfo("","","","","",""));
        this.loggedInUserUid = "";
        this.IS_BETA = (this.afs.firestore.app.options as any).projectId == "betachildpilot" ? true : false;
        this.daycare = undefined;
    }

    // TX FCCN Partnership: the ChildPilot software subscription is free for 12 months, no pricing
    // shown. Single source of truth — sign-up-auth and contract-terms-modal both read this.
    isTxFccnFree(): boolean {
        return !!this.daycare && this.daycare.cpBillingPlan === 'txFccnFree';
    }

    getAPIBaseUrl(): string {
        return this.IS_BETA ? "https://us-central1-betachildpilot.cloudfunctions.net" : "https://us-central1-quickkidz-c0638.cloudfunctions.net";
    }

    getQuickbooksFBRestUrl(): string {
        return "https://us-central1-sportspilotquickbooks.cloudfunctions.net";
    }

    // Data that is set from a potential daycare sign-up
    setSignUpData(daycare: any) {
        this.daycare = daycare;
        if(this.daycare) {
            this.signUpInfo = new SignUpInfo(this.daycare.nameToShow, this.daycare.adminEmail, this.daycare.adminEmail, this.daycare.adminName, this.daycare.phoneNumber, "");
        }
    }
    
    setUID(uid : string): void {
        this.loggedInUserUid = uid;
    }

    // Save json to Session Storage
    saveItemToSessionStorage(key: string, value: any): void {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    // Get an item from Session Storage
    getItemFromSessionStorage(key: string): any {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
    }

    clearSessionStorage(): void {
        sessionStorage.clear();
    }

    async getCPDiscounts() {
        this.cpDiscounts = (await this.afs.collection("cpDiscounts").ref.get().then()).docs.map((d) => { return d.data() });
    }
    
    getDiscountName(discountID: string): string {
        let discount: any = this.cpDiscounts.filter((discount => discount.id == discountID))[0];
        return (discount ? discount.name : "");
    }

    getBillingPlanName(cpBillingPlan: string): string | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return 'ChildPilot Lite';
            }
            case "economy": {
                return "ChildPilot Economy Plus";
            }
            case "business": {
                return "ChildPilot Business Class";
            }
            case "first": {
                return "ChildPilot First Class";
            }
        }
    }
    
    getBillingPlanDocuments(cpBillingPlan: string): number | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return 0;
            }
            case "economy": {
                return 6;
            }
            case "business": {
                return 10;
            }
            case "first": {
                return 20;
            }
        }
    }

    getBillingPlanTexts(cpBillingPlan: string): number | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return 0;
            }
            case "economy": {
                return 500;
            }
            case "business": {
                return 1000;
            }
            case "first": {
                return 5000;
            }
        }
    }

    getBillingPlanChildrenAmt(cpBillingPlan: string): string | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return '0-30';
            }
            case "economy": {
                return '31-64';
            }
            case "business": {
                return '65-99';
            }
            case "first": {
                return '100+';
            }
        }
    }

    getBillingPlanMonthlyCost(cpBillingPlan: string): number | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return 2.50;
            }
            case "economy": {
                return 100;
            }
            case "business": {
                return 140;
            }
            case "first": {
                return 180;
            }
        }
    }

    getBillingPlanMonthlyCostPeriod(cpBillingPlan: string): string | undefined {
        switch(cpBillingPlan) {
            case 'lite': {
                return "Child";
            }
            case "economy": {
                return "Month";
            }
            case "business": {
                return "Month";
            }
            case "first": {
                return "Month";
            }
        }
    }

    // Add director's signature to --contracts/{daycareAlias} in storage
    addImageToStorage(base64String: string, daycareAlias: string): Promise<any> {
        var storageRef = firebase.storage().ref();
        var imageRef = storageRef.child('--contracts/' + daycareAlias)

        return new Promise((resolve, reject) => {
            imageRef.putString(base64String, 'data_url').then(function (uploadTask) {
                uploadTask.ref.getDownloadURL().then(function (downloadURL) {
                    resolve(downloadURL);
                }).catch((err) => {
                    console.log(err);
                    reject(err);
                });

            }).catch((err) => {
                console.log(err);
                reject(err)
            })
        })
    }

    // Calls fb function to register director in /users, /potentialDaycares/{daycareName}/users, and /potentialDaycares/{daycareName}/employees
    registerDirector(uid: string): Promise<any> {
        let headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization' : this.getAuthBearerTokenNoUser(this.daycare.name, this.daycare.inviteKey)
		});
        
        let userInfo: any = {
            uid,
            fName: "",
            lName: "",
            email: this.signUpInfo.adminEmail,
            name: this.signUpInfo.adminName, 
        }

        var fullName = userInfo.name.split(' ');
        userInfo.fName = fullName[0],
        userInfo.lName = fullName.length != 1 ? fullName[fullName.length - 1]: "";

        let options = new RequestOptions({ headers: headers });
        let body = JSON.stringify({daycare: this.daycare, userInfo,timezoneOffset: new Date().getTimezoneOffset()})

        return new Promise((resolve,reject) => {
            this.http.post(this.getAPIBaseUrl() + "/addDirectorToPotentialDaycare",body,options)
            .pipe(map(res => res.json()), timeout(30000))
            .toPromise()
            .then((res) => {
                resolve(res)
            })
            .catch((err) => {
                reject(err)
            })
        })
    }

    // Calls fb function to retrieve daycare obj from /potentialDaycares
    getDaycare(daycareGUID: string, daycareAlias: string, isPotentialDaycare: boolean): Promise<any> {
        let headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization' : this.getAuthBearerTokenNoUserNoDaycareAlias(daycareGUID)
		});
		
        let options = new RequestOptions({ headers: headers });
        let body = JSON.stringify({
            daycareGUID: daycareGUID || "",
            alias: daycareAlias || "", 
            isPotentialDaycare: isPotentialDaycare
        })

        return new Promise((resolve,reject) => {
            this.http.post(this.getAPIBaseUrl() + "/getDayCareInfo",body,options)
            .pipe(map(res => res.json()), timeout(30000))
            .toPromise()
            .then((res) => {
                //console.log(res);
                resolve(res)
            })
            .catch((err) => {
                resolve(null)
            });
        })
    }
    
    // Calls fb function to send email w/ contracts to daycare director & CP team
    sendContract(downloadURL: string): Promise<any> {
        let headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization' : this.getAuthBearerToken()
		});
		
        let options = new RequestOptions({ headers: headers });
        // Explicit 'en-US'/long-month format avoids the ambiguous M/D/Y vs D/M/Y numeric
        // formats toLocaleDateString() produces based on the signer's own browser locale —
        // those get misread by the API's new Date(string) parsing (e.g. "15/10/2026" from a
        // day-first locale is read as an invalid month), producing "Invalid Date" on the signed PDF.
        let signedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        let body = JSON.stringify({downloadURL, daycareAlias: this.daycare.alias, signedDate})

        return new Promise((resolve,reject) => {
            this.http.post(this.getAPIBaseUrl() + "/sendContractEmail",body,options)
            .pipe(map(res => res.json()), timeout(30000))
            .toPromise()
            .then((res) => {
                //console.log(res);
                resolve(res)
            })
            .catch((err) => {
                reject(err);
            });
        })
    }

    // Calls fb function to move daycare document from /potentialDaycares to /daycares
    movePotentialDaycareToActive(daycareName: string): Promise<any> {
        let headers = new Headers({
            'Content-type': 'application/json',
            'Authorization': this.getAuthBearerToken()
        });

        let options = new RequestOptions({ headers });
        let body = JSON.stringify({ daycareName });

        return new Promise((resolve,reject) => {
            this.http.post(this.getAPIBaseUrl() + "/movePotentialDaycareToActive",body,options)
            .pipe(map(res => res.json()), timeout(30000))
            .toPromise()
            .then((res) => {
                //console.log(res);
                resolve(res)
            })
            .catch((err) => {
                reject(err);
            });
        })
    }

    // Calls fb function to delete daycare document
    deleteDaycare(daycareName: string, isPotentialDaycare: boolean): Promise<any> {
        let headers = new Headers({
            'Content-type': 'application/json',
            'Authorization': this.getAuthBearerToken()
        });

        let options = new RequestOptions({ headers });
        let body = JSON.stringify({ daycareName, isPotentialDaycare });

        return new Promise((resolve,reject) => {
            this.http.post(this.getAPIBaseUrl() + "/deleteDaycare",body,options)
            .pipe(map(res => res.json()), timeout(30000))
            .toPromise()
            .then((res) => {
                console.log(res);
                resolve(res)
            })
            .catch((err) => {
                reject(err);
            });
        })
    }

        // Check if credit card info is correct
        authorizeCreditCard(cardNumber: string, cvv: string, expDate: any, name: string, zip: string, daycareAlias: string): Promise<any> {
            return new Promise((resolve, reject) => {
                let headers = new Headers({
                    'Content-Type': 'application/json',
                    'Authorization' : 'Bearer ' + btoa("6WnrpVIrSl3Xrg6IcgAM;" + new Date())
                });
                let options = new RequestOptions({ headers: headers });
                let body = JSON.stringify({ 
                    isChildPilot: true, 
                    cardNumber: cardNumber, 
                    cvv: cvv, 
                    expDate: expDate,
                    name: name,
                    zipCode: zip,
                    daycareAlias
                });
                
                this.http.post(this.getQuickbooksFBRestUrl() + "/authorizeCreditCard", body, options)
                    .pipe(map(res => res.json()), timeout(45000))
                    .toPromise()
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        console.log(error);
                        reject(error);
                    });
            });
        }

    // Calls endpoint that will take Daycare Payment, then make record of Customer, Invoice, and Payment in Quickbooks
    createQuickbooksInvoicePaymentAccount(cardNumber: string, token: string, expDate: Date, cardZip: string, name: string, amount: number, balance: number, daycare: any, adminEmail: string, adminName: string,  cpAccountRep: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let headers = new Headers({
                'Content-Type': 'application/json',
                'Authorization' : 'Bearer ' + btoa("6WnrpVIrSl3Xrg6IcgAM;" + new Date())
            });
            let options = new RequestOptions({ headers: headers });
            let body = JSON.stringify({ 
                payment: {
                    token,
                    zip: cardZip,
                    tokenExpDate: expDate,
                    amount,
                    payerName: name,
                    accountNumber: daycare.accountNumber,
                    customerName: daycare.name,
                    isChildPilot: true,
                    email: adminEmail,
                    paymentMethodName: this.getDefaultPaymentMethodName(cardNumber),
                    type: 1
                },
				fName: (adminName || "").split(" ")[0], 
				lName: (adminName || "").split(" ")[1],
                daycareStreet: daycare.address,
                daycareCity: daycare.city,
                daycareState: daycare.state,
                daycareZip: daycare.zip,
                daycareEmail: daycare.adminEmail, 
                daycarePhone: daycare.phoneNumber,
                balance,
                daycareAccountRep: cpAccountRep,
                isTaxable: daycare.isTaxed
            });
            
            this.http.post(this.getQuickbooksFBRestUrl() + "/authorizeAndCreateNewInvoicePaymentCustomerV2", body, options)
                .pipe(map(res => res.json()), timeout(45000))
                .toPromise()
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    }

    getDefaultPaymentMethodName(cardNumber: string) { 
        let cardType = this.getCardType(cardNumber);
        let last4 = cardNumber.substr(cardNumber.length-4, 4);
        return `${cardType} Ending in ***${last4}`; 
    }


    getCardType(number: string) {
        // VISA
        number = number.replace(/\s/g, '');
        var re = new RegExp("^4");
        if (number.match(re) != null)
            return "VISA";

        // MasterCard 
        // Updated for Mastercard 2017 BINs expansion
        if (/^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(number)) 
            return "MasterCard";

        // Amex
        re = new RegExp("^3[47]");
        if (number.match(re) != null)
            return "Amex";

        // Discover
        re = new RegExp("^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)");
        if (number.match(re) != null)
            return "Discover";

        // Diners
        re = new RegExp("^36");
        if (number.match(re) != null)
            return "Diners";

        // Diners - Carte Blanche
        re = new RegExp("^30[0-5]");
        if (number.match(re) != null)
            return "Diners - Carte Blanche";

        // JCB
        re = new RegExp("^35(2[89]|[3-8][0-9])");
        if (number.match(re) != null)
            return "JCB";

        // Visa Electron
        re = new RegExp("^(4026|417500|4508|4844|491(3|7))");
        if (number.match(re) != null)
            return "Visa Electron";

        return "";
    }

    getAuthBearerTokenNoUser(daycareName: string, daycareInviteKey: string) {
		return 'Bearer ' + btoa(daycareName + ";" + daycareInviteKey + ";" + new Date().toISOString());
    }
    
    getAuthBearerTokenNoUserNoDaycareAlias(GUID: string) {
		return 'Bearer ' + btoa(GUID + ";" + new Date().toISOString());
    }
    
    getAuthBearerToken() {
        return 'Bearer ' + btoa("demodaycare;" + (this.loggedInUserUid ? this.loggedInUserUid :"") + ';' + new Date().toISOString());
    }

    getAccountingBearerToken() {
        return btoa('Bearer 6WnrpVIrSl3Xrg6IcgAM;' + new Date().toLocaleDateString());
    }

    //retrieve a querystring param value out of the url
	getQueryStringParams(url: string, param: string) {
		param = param.replace(/[\[]/, "\\\[").replace(/[\]]/, "\\\]");
		var regexS = "[\\?&]" + param + "=([^&#]*)";
		var regex = new RegExp(regexS);
		var results = regex.exec(url);
		if (results === null)
			return "";
		else
			return results[1];
    }
    
    // Calls CP webApi to test this phone number using Twilio's lookups api
    async lookupPhone(phoneNumber: string): Promise<any> {
        try {
            let headers = new Headers({
                'Content-Type': 'application/json',
                'Authorization' : this.getAuthBearerTokenNoUserNoDaycareAlias(this.afs.createId())
            });
            let options = new RequestOptions({ headers: headers });

            let res = await this.http.get(`https://childpilot-api.firebaseapp.com/api/v1/lookupPhoneCarrier/${phoneNumber}`, options)
                .pipe(map(res => res.json()))
                .toPromise();

            return res;
        } catch (e) {
            console.log(e);
            throw e;
        }
    }

    	// Get a moment for a given date.
	getJSDate(date: string | Date | any): Date {
		if (!date || date == 'Invalid Date') {
            return moment().toDate();
        }

		// Check if date is a string.
		if (typeof date === 'string') {
			return moment(date, ['MM-DD-YYYY', 'M-D-YYYY', '"YYYY-MM-DD hh:mm:ss A Z"']).toDate();
		}

		// Check if date is a Date
		if (date instanceof Date) {
			return moment(date).toDate();
		}

		// Might be a timestamp?
		if (date && date.toDate) {
			return moment(date.toDate()).toDate();
		}

		// Last, check for seconds/nanoseconds on obj, a stringified and unstringified timestamp will lose toDate() function.
        if (date && (date.seconds || date._seconds)) {
            let seconds = date.seconds || date._seconds || 0;
			let nanoseconds = date.nanoseconds || 0;
			let miliseconds = seconds * 1000 + nanoseconds / 1000000;
            return moment.unix(miliseconds / 1000).toDate();
		}
		
		// If it makes hit here just return a timestamp.
		return moment().toDate();
	}
}